import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { X, Search } from 'lucide-react';
import { BACKEND_URL } from 'apiConfig';

const API = BACKEND_URL;

const STAGES = [
  { value: 'medical_student', label: 'Medical student' },
  { value: 'resident', label: 'Resident' },
  { value: 'fellow', label: 'Fellow' },
  { value: 'attending', label: 'Attending / Consultant' },
  { value: 'app_pa_np', label: 'APP / PA / NP' },
  { value: 'researcher', label: 'Researcher' },
  { value: 'other', label: 'Other' },
];

const YEARS = [
  { value: 'in_training', label: 'In training' },
  { value: '0_2', label: '0\u20132 years' },
  { value: '3_5', label: '3\u20135 years' },
  { value: '6_10', label: '6\u201310 years' },
  { value: '11_20', label: '11\u201320 years' },
  { value: '21_plus', label: '21+ years' },
];

const SETTINGS = [
  { value: 'academic', label: 'Academic medical center' },
  { value: 'community_hospital', label: 'Community hospital' },
  { value: 'private_practice', label: 'Private practice' },
  { value: 'group_practice', label: 'Group practice' },
  { value: 'government_va', label: 'Government / VA / public health' },
  { value: 'research', label: 'Research-focused' },
  { value: 'other', label: 'Other' },
];

const ENVIRONMENTS = [
  { value: 'inpatient', label: 'Inpatient' },
  { value: 'outpatient', label: 'Outpatient' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'not_clinical', label: 'Not primarily clinical' },
];

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'India', 'Germany',
  'France', 'Brazil', 'Japan', 'South Korea', 'China', 'Italy', 'Spain',
  'Netherlands', 'Sweden', 'Switzerland', 'Singapore', 'Israel', 'South Africa',
  'Mexico', 'Saudi Arabia', 'UAE', 'Nigeria', 'Egypt', 'Turkey', 'Pakistan',
  'New Zealand', 'Ireland', 'Belgium', 'Austria', 'Denmark', 'Norway', 'Finland',
  'Poland', 'Portugal', 'Czech Republic', 'Argentina', 'Colombia', 'Chile', 'Other',
];

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
  'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
  'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
  'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
  'Wisconsin','Wyoming','District of Columbia',
];

// Searchable dropdown with filter
function SearchableSelect({ value, onValueChange, options, placeholder, testId }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(o => {
      const label = typeof o === 'string' ? o : o.label;
      return label.toLowerCase().includes(q);
    });
  }, [options, search]);

  const displayLabel = useMemo(() => {
    if (!value) return null;
    const opt = options.find(o => (typeof o === 'string' ? o : o.value) === value);
    return opt ? (typeof opt === 'string' ? opt : opt.label) : value;
  }, [value, options]);

  return (
    <div className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex h-9 w-full items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>{displayLabel || placeholder}</span>
        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-56 overflow-hidden">
          <div className="px-2 pt-2 pb-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-44">
            {filtered.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No results</div>}
            {filtered.map(o => {
              const val = typeof o === 'string' ? o : o.value;
              const label = typeof o === 'string' ? o : o.label;
              return (
                <button
                  key={val}
                  type="button"
                  onClick={() => { onValueChange(val); setOpen(false); setSearch(''); }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 ${value === val ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-700'}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {open && <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setSearch(''); }} />}
    </div>
  );
}

// Subspecialty multi-select (tokenized, max 3)
function SubspecialtySelect({ specialties, selectedSpecialty, value, onChange, testId }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const subspecialtyOptions = useMemo(() => {
    if (!selectedSpecialty || !specialties) return [];
    const spec = specialties.find(s => s.id === selectedSpecialty);
    return (spec?.subspecialties || []).filter(s => !s.id.endsWith('_general_core'));
  }, [specialties, selectedSpecialty]);

  const filtered = useMemo(() => {
    if (!search) return subspecialtyOptions;
    const q = search.toLowerCase();
    return subspecialtyOptions.filter(s => (s.label || s.name || '').toLowerCase().includes(q));
  }, [subspecialtyOptions, search]);

  const addSub = (id) => {
    if (value.length >= 3) return;
    if (!value.includes(id)) onChange([...value, id]);
    setOpen(false);
    setSearch('');
  };

  const removeSub = (id) => onChange(value.filter(v => v !== id));

  const getLabel = (id) => {
    const sub = subspecialtyOptions.find(s => s.id === id);
    return sub ? (sub.label || sub.name) : id;
  };

  if (!selectedSpecialty) {
    return <p className="text-xs text-gray-400 italic">Select a primary specialty first</p>;
  }

  return (
    <div data-testid={testId}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map(id => (
            <Badge key={id} className="bg-blue-50 text-blue-700 text-xs font-normal pl-2 pr-1 py-0.5 gap-1">
              {getLabel(id)}
              <button type="button" onClick={() => removeSub(id)} className="hover:bg-blue-100 rounded-full p-0.5">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      {value.length < 3 && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="flex h-9 w-full items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <span className="text-gray-400">Add subspecialty ({3 - value.length} remaining)</span>
            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
          {open && (
            <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-48 overflow-hidden">
              <div className="px-2 pt-2 pb-1">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search subspecialties..."
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400" autoFocus />
                </div>
              </div>
              <div className="overflow-y-auto max-h-36">
                {filtered.filter(s => !value.includes(s.id)).map(s => (
                  <button key={s.id} type="button" onClick={() => addSub(s.id)}
                    className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-blue-50">
                    {s.label || s.name}
                  </button>
                ))}
                {filtered.filter(s => !value.includes(s.id)).length === 0 && (
                  <div className="px-3 py-2 text-xs text-gray-400">{subspecialtyOptions.length === 0 ? 'No subspecialties available' : 'No more options'}</div>
                )}
              </div>
            </div>
          )}
          {open && <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setSearch(''); }} />}
        </div>
      )}
    </div>
  );
}

export default function PracticeDetailsModal({ open, onOpenChange, profile, onSave, betaSpecialty }) {
  const [specialties, setSpecialties] = useState([]);
  const [form, setForm] = useState({
    primary_specialty: '',
    specialty_2: '',
    subspecialties: ['', '', ''],
    current_stage: '',
    years_in_practice: '',
    country: '',
    state_province: '',
    city: '',
    practice_setting: '',
    clinical_environment: '',
  });

  // Load specialties
  useEffect(() => {
    fetch(`${API}/api/config/specialties`)
      .then(r => r.json())
      .then(data => setSpecialties(data.specialties || []))
      .catch(() => {});
  }, []);

  // Seed from existing profile or beta specialty
  useEffect(() => {
    if (profile) {
      // When restoring from saved profile, ensure subspecialties is a 3-element array
      const subs = profile.subspecialties || [];
      setForm(prev => ({
        ...prev,
        ...profile,
        subspecialties: [subs[0] || '', subs[1] || '', subs[2] || ''],
      }));
    }
  }, [profile]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const showStateDropdown = form.country === 'United States';

  const handleSave = () => {
    // Strip empty strings so we only store actual answers
    const cleaned = {};
    for (const [k, v] of Object.entries(form)) {
      if (k === 'subspecialties') {
        // Filter out empty subspecialty strings
        const filled = (v || []).filter(s => s && s.trim());
        if (filled.length > 0) cleaned[k] = filled;
      } else if (v) {
        cleaned[k] = v;
      }
    }
    onSave(Object.keys(cleaned).length > 0 ? cleaned : null);
    onOpenChange(false);
  };

  const handleSkip = () => onOpenChange(false);

  const specialtyOptions = specialties.map(s => ({ value: s.id, label: s.label || s.name }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto p-0" data-testid="practice-details-modal">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-lg">Practice Details</DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Help us tailor LitCurrent to your setting. All fields are optional.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-2 space-y-5">
          {/* Row 1: Specialty 1 + Specialty 2 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Specialty 1</Label>
              <Input
                value={form.primary_specialty}
                onChange={e => update('primary_specialty', e.target.value)}
                placeholder=""
                data-testid="practice-specialty-1"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Specialty 2</Label>
              <Input
                value={form.specialty_2 || ''}
                onChange={e => update('specialty_2', e.target.value)}
                placeholder=""
                data-testid="practice-specialty-2"
              />
            </div>
          </div>

          {/* Row 2: Subspecialty 1, 2, 3 — first two side by side, third below or inline */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Subspecialty 1</Label>
              <Input
                value={(form.subspecialties && form.subspecialties[0]) || ''}
                onChange={e => {
                  const subs = [...(form.subspecialties || ['', '', ''])];
                  subs[0] = e.target.value;
                  update('subspecialties', subs);
                }}
                placeholder=""
                data-testid="practice-subspecialty-1"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Subspecialty 2</Label>
              <Input
                value={(form.subspecialties && form.subspecialties[1]) || ''}
                onChange={e => {
                  const subs = [...(form.subspecialties || ['', '', ''])];
                  subs[1] = e.target.value;
                  update('subspecialties', subs);
                }}
                placeholder=""
                data-testid="practice-subspecialty-2"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Subspecialty 3</Label>
              <Input
                value={(form.subspecialties && form.subspecialties[2]) || ''}
                onChange={e => {
                  const subs = [...(form.subspecialties || ['', '', ''])];
                  subs[2] = e.target.value;
                  update('subspecialties', subs);
                }}
                placeholder=""
                data-testid="practice-subspecialty-3"
              />
            </div>
          </div>

          {/* Row 2: Stage + Years */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Current Stage</Label>
              <Select value={form.current_stage} onValueChange={v => update('current_stage', v)}>
                <SelectTrigger data-testid="practice-stage-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
                <SelectContent>
                  {STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Years in Practice (after training)</Label>
              <Select value={form.years_in_practice} onValueChange={v => update('years_in_practice', v)}>
                <SelectTrigger data-testid="practice-years-select"><SelectValue placeholder="Select range" /></SelectTrigger>
                <SelectContent>
                  {YEARS.map(y => <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 3: Country + State/City */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Country</Label>
              <SearchableSelect
                value={form.country}
                onValueChange={v => { update('country', v); update('state_province', ''); }}
                options={COUNTRIES}
                placeholder="Select country"
                testId="practice-country-select"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">State / Province</Label>
              {showStateDropdown ? (
                <SearchableSelect
                  value={form.state_province}
                  onValueChange={v => update('state_province', v)}
                  options={US_STATES}
                  placeholder="Select state"
                  testId="practice-state-select"
                />
              ) : (
                <Input
                  value={form.state_province}
                  onChange={e => update('state_province', e.target.value)}
                  placeholder="Province / region"
                  data-testid="practice-state-input"
                />
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">City</Label>
              <Input
                value={form.city}
                onChange={e => update('city', e.target.value)}
                placeholder="City"
                data-testid="practice-city-input"
              />
            </div>
          </div>

          {/* Row 4: Setting + Environment */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Main Practice Setting</Label>
              <Select value={form.practice_setting} onValueChange={v => update('practice_setting', v)}>
                <SelectTrigger data-testid="practice-setting-select"><SelectValue placeholder="Select setting" /></SelectTrigger>
                <SelectContent>
                  {SETTINGS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Clinical Environment</Label>
              <Select value={form.clinical_environment} onValueChange={v => update('clinical_environment', v)}>
                <SelectTrigger data-testid="practice-env-select"><SelectValue placeholder="Select environment" /></SelectTrigger>
                <SelectContent>
                  {ENVIRONMENTS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50/50">
          <Button variant="ghost" onClick={handleSkip} data-testid="practice-skip-btn" className="text-gray-500">
            Skip for now
          </Button>
          <Button onClick={handleSave} data-testid="practice-save-btn">
            Save Details
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
